import { createQueryKeys } from "@lukemorales/query-key-factory";

import MasterApiBase from "./master-api.base";

export default class AudiencesApi extends MasterApiBase {
	/**
	 * Creates audience members with the provided data.
	 *
	 * @param {Object} data - The data for creating audience members.
	 * @param {Array<{ id: string }>} data.audiences - An array of audience objects, each containing an `id`.
	 * @param {Object} data.searchCondition - The search conditions as key-value pairs.
	 * @param {string} data.name - The name associated with the audience members.
	 * @returns {Promise} A promise that resolves when the audience members are created.
	 */
	createAudienceMembers = function (data) {
		return this.axios.post(`${this.endpoints.audiences}/members`, data);
	};

	createDefaultAudience = function (data) {
		return this.axios.post(`${this.endpoints.audiences}`, data);
	};

	createLotteryAudience = function (data) {
		return this.axios.post(`${this.endpoints.audiences}/lotteries`, data);
	};

	createCampaignAudience = function (data) {
		return this.axios.post("/audiences-campaign", data);
	};

	createSurveyAudience = function (data) {
		return this.axios.post("/audiences-survey", data);
	};

	/**
	 * Searches for audience members based on the provided parameters.
	 *
	 * @param {Object} params - The search parameters as key-value pairs.
	 * @returns {Promise<Array<{ id: string }>>} A promise that resolves to an array of objects, each containing an `id` property.
	 */
	searchAudienceMembers = function (params) {
		return this.axios.get(`${this.endpoints.audiences}/members`, {
			params,
		});
	};

	/**
	 * Searches for audience members based on the provided parameters.
	 *
	 * @param {Object} data - The search parameters as key-value pairs.
	 * @returns {Promise<Array<{ id: string }>>} A promise that resolves to an array of objects, each containing an `id` property.
	 */
	searchAudienceEvents = function (data) {
		return this.axios.post(`${this.endpoints.audiences}/events`, data);
	};

	/**
	 * Deletes an audience by its ID.
	 *
	 * @param {number} id - The ID of the audience to delete.
	 * @returns {Promise} A promise that resolves when the audience is deleted.
	 */
	deleteAudience = function (id) {
		return this.axios.delete(`${this.endpoints.audiences}/${id}`);
	};

	/**
	 * Searches for audience members based on their visit history.
	 *
	 * @param {Object} params - The search parameters.
	 * @returns {Promise<Array>} A promise that resolves to an array of audience members.
	 */
	searchAudienceMemberVisit = function (params) {
		return this.axios.get(`${this.endpoints.audiences}/member-visit`, {
			params,
		});
	};

	/**
	 * Creates an audience based on member visit history.
	 *
	 * @param {Object} data - The data for creating the audience.
	 * @returns {Promise} A promise that resolves when the audience is created.
	 */
	createAudienceMemberVisit = function (data) {
		return this.axios.post(`${this.endpoints.audiences}/member-visit`, data);
	};

	searchAudienceSubscription = function (params) {
		return this.axios.get(`${this.endpoints.audiences}/subscription`, {
			params,
		});
	};

	createAudienceSubscription = function (data) {
		return this.axios.post(`${this.endpoints.audiences}/subscription`, data);
	};

	/**
	 * Retrieves all audiences based on the provided parameters.
	 *
	 * @private
	 * @param {Object} [params] - Optional parameters for filtering the audiences.
	 * @param {string} [params.type] - The type of audience to filter by.
	 * @returns {Promise<Audience>} A promise that resolves to an array of audiences.
	 */
	#findAll = function (params) {
		return this.axios.get(this.endpoints.audiences, {
			params,
		});
	};

	queryKeys = createQueryKeys("audiences", {
		/**
		 * Generates a query object for listing audiences based on the provided filters.
		 *
		 * @param {Object} [filters] - Optional filters to apply when listing audiences.
		 * @returns {Object} An object containing the query key and the query function.
		 * @returns {Array} return.queryKey - The key used for caching the query, based on the filters.
		 * @returns {Function} return.queryFn - The function that executes the query to list audiences.
		 */
		list: (filters) => ({
			queryKey: [{ filters }],
			queryFn: () => this.#findAll(filters),
		}),
	});
}
